import { companyPath, groupPath } from 'helper/recordPaths';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import DeletedTaskTable from 'components/tasks/DeletedTaskTable';
import PageForm from 'components/pageForm/PageForm';
import TaskFilterPanel from 'components/tasks/TaskFilterPanel';
import TaskKanban, { TaskKanbanSkeleton } from 'components/tasks/TaskKanban';
import PeopleSelect from 'components/tasks/PeopleSelect';
import TaskTable from 'components/tasks/TaskTable';
import TasksToolbar from 'components/tasks/TasksToolbar';
import UndoToast from 'components/toasts/UndoToast';
import { SUPER_ADMIN_ROLE } from 'helper/roles';
import { omitKey, wait } from 'helper/promises';
import { apiErrorText, isCanceledRequest } from 'helper/apiErrorText';
import useAppointmentPanel from 'hooks/useAppointmentPanel';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import Usefetchdata from 'hooks/usefetchdata';
import useLeavingItems from 'hooks/useLeavingItems';
import useOpenedTasks from 'hooks/useOpenedTasks';
import usePlanningChanged from 'hooks/usePlanningChanged';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged, { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import {
  DUE_FILTERS,
  DUE_PARAMS,
  KANBAN_COLUMNS,
  boardColumns,
  describeRow,
  dueMatches,
  dueOnForColumn,
  groupRows,
  originShown,
  rowOriginOf,
  keepRow,
  taskRows,
} from './tasksItems';
import { eventCategoryText } from 'constants/eventCategories';

const VIEW_KEY = 'tasks-view';
// The old key was shared by every user of the browser.
const LEGACY_PEOPLE_KEY = 'tasks-people';
const peopleKey = (userId) => `tasks-people:${userId}`;
const filtersKey = (userId) => `tasks-filters:${userId}`;
const collapsedKey = (userId) => `tasks-collapsed:${userId}`;
// Only these groups fold: the late and today ones always show.
const FOLDABLE_GROUPS = ['tomorrow', 'later'];
const TABS = ['todo', 'done', 'deleted'];
const DEFAULT_FILTERS = { due: 'all', day: null, types: [], mentioned: false, created: false };
// A kept value that is not readable is ignored, field by field.
const validKept = (kept) => {
  if (!kept || typeof kept !== 'object' || Array.isArray(kept)) return {};
  const { tab, filters } = kept;
  const out = {};
  if (TABS.includes(tab)) out.tab = tab;
  if (filters && typeof filters === 'object') {
    if (filters.due === 'all' || DUE_FILTERS.includes(filters.due)) out.due = filters.due;
    if (filters.day === null || typeof filters.day === 'string') out.day = filters.day;
    if (Array.isArray(filters.types) && filters.types.every((type) => typeof type === 'string')) out.types = filters.types;
    if (typeof filters.mentioned === 'boolean') out.mentioned = filters.mentioned;
    if (typeof filters.created === 'boolean') out.created = filters.created;
  }
  return out;
};
// Codes meaning the kept choice is not valid any more.
const PEOPLE_REFUSED = ['INVALID_PEOPLE', 'TEAM_VIEW_FORBIDDEN'];
const readStored = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};
const forget = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing to forget.
  }
};
const store = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable: the choice is not kept.
  }
};

// What a tick says, by what the row is: done, or reopened.
const TOASTS = {
  task: ['doneTask', 'reopenTask'],
  appointment: ['doneAppointment', 'reopenAppointment'],
  callback: ['doneCallback', 'reopenCallback'],
};
const MOVED = { today: 'movedToday', tomorrow: 'movedTomorrow', later: 'movedLater' };

/**
 * « Mes tâches » of the CRM prototype: the table of the tasks to do by due
 * day, or done, and the same tasks in a kanban. An agency manager also looks
 * at the tasks of colleagues. Its data comes from the api (server/crm/crmApi);
 * `?echeance=retard` opens it on the late tasks. A task opens in its panel.
 */
const TasksPage = () => {
  const { text, language } = useText('tasks');
  const { userData } = Usefetchdata();
  const navigate = useNavigate();
  const location = useLocation();
  const { call, me, ready } = useCrmContext();
  const { openTask, openTaskForm } = useTaskPanels();
  const { openAppointment } = useAppointmentPanel();
  const notify = useNotifyTasksChanged();
  const manager = userData?.role === SUPER_ADMIN_ROLE;
  const [params, setParams] = useSearchParams();
  const { slideOverShow } = UseContext();
  const taskParam = params.get('task');
  // The task whose panel the address asked for or the page opened, and whether
  // a slide-over has been seen open since: closing it drops ?task=.
  const linkedTask = useRef(null);
  const sawSlideOver = useRef(false);
  // `chosen` is undefined until the manager picks (or a refusal clears) a view.
  const [chosen, setChosen] = useState(undefined);
  const storedKey = me ? peopleKey(me.id) : null;
  const people = useMemo(
    () => (chosen !== undefined ? chosen : storedKey ? readStored(storedKey, null) : null),
    [chosen, storedKey],
  );
  const peopleNow = useRef(null);
  peopleNow.current = manager ? people : null;
  const viewing = useMemo(() => (manager && people ? people : me ? [me.id] : null), [manager, people, me]);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState('todo');
  const [query, setQuery] = useState('');
  // `mentioned`: the tasks where one is only mentioned, off by default, « Mentions » shows them.
  // `created`: the tasks one created for someone else, to follow them, off by default.
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    due: DUE_PARAMS[params.get('echeance')] ?? 'all',
  }));
  // The tab and filters kept for this user are read once, when it is known;
  // nothing is written before that, or the defaults would overwrite them.
  const [restoredFor, setRestoredFor] = useState(null);
  useEffect(() => {
    if (!me?.id || restoredFor === me.id) return;
    const { tab: keptTab, ...keptFilters } = validKept(readStored(filtersKey(me.id), null));
    if (keptTab) setTab(keptTab);
    // An address asking for a due filter wins over the kept one.
    if (DUE_PARAMS[params.get('echeance')]) delete keptFilters.due;
    setFilters((current) => ({ ...current, ...keptFilters }));
    setRestoredFor(me.id);
  }, [me?.id, restoredFor, params]);
  useEffect(() => {
    if (!me?.id || restoredFor !== me.id) return;
    store(filtersKey(me.id), { tab, filters });
  }, [me?.id, restoredFor, tab, filters]);
  // The panel marks a task opened (TaskPanel); the list only reads the marks.
  const { isOpened } = useOpenedTasks(me?.id);
  const [collapsed, setCollapsed] = useState([]);
  useEffect(() => {
    const kept = me?.id ? readStored(collapsedKey(me.id), []) : [];
    setCollapsed(Array.isArray(kept) ? kept.filter((id) => FOLDABLE_GROUPS.includes(id)) : []);
  }, [me?.id]);
  const toggleGroup = (id) => {
    if (!FOLDABLE_GROUPS.includes(id)) return;
    const next = collapsed.includes(id) ? collapsed.filter((item) => item !== id) : [...collapsed, id];
    setCollapsed(next);
    if (me?.id) store(collapsedKey(me.id), next);
  };
  const [filtering, setFiltering] = useState(false);
  const [view, setView] = useState(() => readStored(VIEW_KEY, 'list'));
  // What was ticked a moment ago, shown before the api answers, and the rows
  // lit green until they leave.
  const [doneNow, setDoneNow] = useState({});
  const [ticked, setTicked] = useState({});
  const { leave, isLeaving, onLeaveEnd } = useLeavingItems();
  const kanban = useRef(null);

  const fetchTasks = useCallback(() => call(crmApi.getTasks, { people: viewing }), [call, viewing]);
  const load = useCallback(() => {
    setFailed(false);
    return fetchTasks().then(setData, (error) => {
      // A kept view the api refuses: back to oneself, which reloads once.
      if (peopleNow.current && PEOPLE_REFUSED.includes(error?.response?.data?.errorCode)) {
        forget(storedKey);
        setChosen(null);
        return;
      }
      if (!isCanceledRequest(error)) setFailed(true);
    });
  }, [fetchTasks, storedKey]);
  useEffect(() => {
    if (!ready) return;
    forget(LEGACY_PEOPLE_KEY);
    setData(null);
    load();
  }, [load, ready]);
  // A task changed in its panel or its form, an appointment in the planning.
  useTasksChanged(load);
  usePlanningChanged(load);

  const addTask = useCallback(() => openTaskForm({ defaults: { type: 'todo' } }), [openTaskForm]);
  const fail = useCallback(
    (error) => {
      const message = apiErrorText(error, { language, text, group: 'errors' });
      if (message) toast.error(message);
    },
    [language, text],
  );
  const openRow = (row) => {
    if (row.kind === 'task') {
      linkedTask.current = row.id;
      setParams((current) => new URLSearchParams({ ...Object.fromEntries(current), task: row.id }), { replace: true });
      openTask(row.id);
    }
    // A Google event opens in Google Calendar, as on the home page.
    else if (row.externalLink) window.open(row.externalLink, '_blank', 'noopener');
    else if (row.kind === 'appointment') openAppointment(row.id);
    // A callback is called back from the prospection, on its company.
    else if (row.kind === 'callback' && row.company?.id)
      navigate(`/prospection?${new URLSearchParams({ prospect: row.company.id })}`);
  };
  // The record's back button returns to this list as it was filtered.
  const openCompany = (target) =>
    navigate(target.kind === 'group' ? groupPath(target.id) : companyPath(target.id), {
      state: { backTo: `${location.pathname}${location.search}` },
    });

  // A direct link /tasks?task=<id> opens that task's panel.
  useEffect(() => {
    if (!taskParam || taskParam === linkedTask.current) return;
    linkedTask.current = taskParam;
    openTask(taskParam);
  }, [taskParam, openTask]);
  useEffect(() => {
    if (slideOverShow) {
      sawSlideOver.current = true;
      return;
    }
    if (!sawSlideOver.current) return;
    sawSlideOver.current = false;
    linkedTask.current = null;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('task');
        return next;
      },
      { replace: true },
    );
  }, [slideOverShow, setParams]);
  const pickPeople = (next) => {
    setChosen(next);
    store(storedKey, next);
  };

  // « N » adds a task, out of a field.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key.toLowerCase() !== 'n' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (slideOverShow || filtering) return;
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      addTask();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addTask, slideOverShow, filtering]);

  const pickView = (next) => {
    setView(next);
    store(VIEW_KEY, next);
  };

  const now = new Date();
  const rows = useMemo(
    () => (data ? taskRows(data, text, (category) => eventCategoryText(language, category, 'title')) : { todo: [], done: [] }),
    [data, text, language],
  );
  // One's own list (a manager's too, when looking at themselves).
  const ownView = Boolean(me) && viewing?.length === 1 && viewing[0] === me.id;
  const mentionsOf = ownView ? me.id : null;
  const keep = (row) => keepRow(row, { query, ...filters, mentionsOf, now });
  const describe = (row) => describeRow(row, { now, text });
  // « Supprimées » draws its own list (DeletedTaskTable): no row of the tasks read here.
  // It exists in the list view only: the kanban, and its filter counts, keep the
  // tasks read here even when that tab was left selected.
  const deletedTab = view === 'list' && tab === 'deleted';
  const tabRows = deletedTab ? [] : rows[tab] ?? rows.todo;
  const groups = groupRows(tabRows.filter(keep).map(describe), tab, now);
  const board = Object.fromEntries(
    Object.entries(boardColumns(rows, keep, now)).map(([column, cards]) => [column, cards.map(describe)]),
  );
  const filterCount = (filters.due !== 'all') + (filters.types.length > 0) + filters.mentioned + filters.created;
  const countFor = (key, id) =>
    tabRows.filter((row) => {
      if (key === 'mentioned' || key === 'created') return rowOriginOf(row, mentionsOf) === key;
      if (mentionsOf && !originShown(rowOriginOf(row, mentionsOf), filters)) return false;
      return key === 'due' ? dueMatches(row, id, filters.day, now) : row.kind === id;
    }).length;

  // A task never opened is lit; the ones done, and rows that are no task, are not.
  const isFresh = (row) => Boolean(me) && row.kind === 'task' && !row.doneAt && !isOpened(row.id);
  const isDone = (row) => (row.key in doneNow ? doneNow[row.key] : Boolean(row.doneAt));
  const forgetTick = (key) => {
    setDoneNow((current) => omitKey(current, key));
    setTicked((current) => omitKey(current, key));
  };
  const saveDone = (row, done) => {
    if (row.kind === 'appointment') return call(crmApi.setAppointmentDone, { id: row.id, done });
    if (row.kind === 'callback') return call(crmApi.setCallbackDone, { id: row.id, done });
    return call(crmApi.setTaskDone, { id: row.id, done });
  };

  const undo = async (row) => {
    try {
      await saveDone(row, false);
      setData(await fetchTasks());
      notify();
    } catch (error) {
      fail(error);
    }
  };

  // Ticked, the row shows it at once (box, struck title, green), long enough
  // to be seen while the api answers, then fades out: it goes to the other
  // tab. A tick in the kanban moves the card instead.
  const toggle = async (row) => {
    const done = !isDone(row);
    setDoneNow((current) => ({ ...current, [row.key]: done }));
    if (done) setTicked((current) => ({ ...current, [row.key]: true }));
    try {
      await Promise.all([saveDone(row, done), wait(done ? 700 : 250)]);
      const next = await fetchTasks();
      leave(row.key, () => {
        setData(next);
        forgetTick(row.key);
        notify();
      });
      const [doneKey, reopenKey] = TOASTS[row.kind];
      if (done) {
        toast.success(<UndoToast message={text(doneKey)} undoLabel={text('undo')} onUndo={() => undo(row)} />);
      } else toast.success(text(reopenKey));
    } catch (error) {
      forgetTick(row.key);
      fail(error);
    }
  };

  const assign = async (row, assigneeId) => {
    try {
      await call(crmApi.assignTask, { id: row.id, assigneeId });
      setData(await fetchTasks());
      notify();
      const employee = data.team.find((member) => member.id === assigneeId);
      toast.success(text('assigned', { name: `${employee.firstname} ${employee.lastname}` }));
    } catch (error) {
      fail(error);
    }
  };

  // A card dropped in the kanban: it lands at once (the board has captured
  // where the cards were), then the api is told.
  const move = async (row, column) => {
    const dueOn = dueOnForColumn(column, now);
    setData((current) => ({
      ...current,
      tasks: current.tasks.map((task) =>
        task.id !== row.id
          ? task
          : column === 'done'
            ? { ...task, doneAt: new Date().toISOString() }
            : { ...task, dueOn, doneAt: null },
      ),
    }));
    try {
      if (column === 'done') await call(crmApi.setTaskDone, { id: row.id, done: true });
      else await call(crmApi.moveTask, { id: row.id, dueOn });
      toast.success(text(column === 'done' ? 'doneTask' : MOVED[column]));
      notify();
    } catch (error) {
      fail(error);
      kanban.current?.capture();
      load();
    }
  };

  const emptyState = () => {
    if (query.trim() || (!deletedTab && filterCount)) {
      return { glyph: 'searchX', title: text('emptyFilteredTitle'), description: text('emptyFilteredText') };
    }
    return tab === 'todo'
      ? { glyph: 'clock', title: text('emptyTodoTitle'), description: text('emptyTodoText') }
      : { glyph: 'badgeCheck', title: text('emptyDoneTitle'), description: text('emptyDoneText') };
  };

  const team = data?.team ?? [];
  const title = (() => {
    if (!manager || !data || (viewing?.length === 1 && viewing[0] === me?.id)) return text('titleMine');
    if (viewing === 'team') return text('titleTeam');
    const names = team
      .filter((person) => viewing.includes(person.id))
      .map((person) => (person.id === me?.id ? text('titleMe') : person.firstname));
    return names.length ? text('titlePeople', { names: names.join(', ') }) : text('titleMine');
  })();

  return (
    <PageForm title={title}>
      <div className="flex min-h-0 flex-1 flex-col">
        <TasksToolbar
          view={view}
          tab={tab}
          // What « À faire » holds by default: one's mentions in others' tasks left out.
          todoCount={data ? rows.todo.filter((row) => !mentionsOf || rowOriginOf(row, mentionsOf) === 'own').length : undefined}
          query={query}
          filterCount={filterCount}
          onTab={setTab}
          onQuery={setQuery}
          onView={pickView}
          onFilter={() => setFiltering(true)}
          onAdd={addTask}
          people={
            manager && data ? (
              <PeopleSelect team={team} meId={me?.id} value={viewing} onChange={pickPeople} />
            ) : null
          }
        />
        <main className="flex min-h-0 flex-1 flex-col border-t border-gray-100">
          {failed && !(view === 'list' && deletedTab) && (
            <CompactEmptyState title={text('loadError')} action={{ label: text('retry'), onClick: load }} />
          )}
          {/* The view to come, in its own shapes. */}
          {!data && !failed && view === 'kanban' && <TaskKanbanSkeleton order={KANBAN_COLUMNS} />}
          {view === 'list' && deletedTab && (
            <DeletedTaskTable call={call} query={query} onOpenCompany={openCompany} onRestored={notify} />
          )}
          {!data && !failed && view === 'list' && !deletedTab && (
            <TaskTable groups={null} rowState={() => ({})} team={team} meId={me?.id} />
          )}
          {data && view === 'kanban' && (
            <TaskKanban
              ref={kanban}
              columns={board}
              order={KANBAN_COLUMNS}
              team={team}
              meId={me?.id}
              onMove={move}
              isFresh={isFresh}
              onOpen={openRow}
              onAssign={assign}
              onAdd={(column) =>
                openTaskForm({
                  defaults: { type: 'todo', dueOn: dueOnForColumn(column, new Date()), done: column === 'done' },
                })
              }
            />
          )}
          {data && view === 'list' && !deletedTab && !groups.length && <CompactEmptyState {...emptyState()} />}
          {data && view === 'list' && !deletedTab && groups.length > 0 && (
            <TaskTable
              groups={groups}
              collapsed={collapsed}
              onToggleGroup={toggleGroup}
              rowState={(row) => ({
                fresh: isFresh(row),
                done: isDone(row),
                ticked: Boolean(ticked[row.key]),
                leaving: isLeaving(row.key),
                onLeaveEnd: onLeaveEnd(row.key),
              })}
              team={team}
              meId={me?.id}
              onToggle={toggle}
              onOpen={openRow}
              onOpenCompany={openCompany}
              onAssign={assign}
            />
          )}
        </main>
      </div>
      <TaskFilterPanel
        isOpen={filtering}
        onClose={() => setFiltering(false)}
        filters={filters}
        // Only one's own list holds tasks where one is just mentioned.
        showMentioned={ownView}
        onChange={(change) => setFilters((current) => ({ ...current, ...change }))}
        counts={countFor}
      />
    </PageForm>
  );
};

export default TasksPage;
